import { parseColumnDateRangeFilter } from "@/utils/dateRangeFilter";
import { ordinal } from "@/utils/deadlineDay";

const EUROPE_BERLIN = "Europe/Berlin";

/**
 * "19th August" — day-of-month ordinal + full month name, in Europe/Berlin.
 * Used by the Homepage v2 header date (design doc §7.1); year is deliberately
 * omitted, matching the mock.
 */
export function formatDayMonthEuropeBerlin(input: Date | string | number = new Date()): string {
    const d = input instanceof Date ? input : new Date(input);
    if (Number.isNaN(d.getTime())) return "";
    const day = Number(
        new Intl.DateTimeFormat("en-US", { timeZone: EUROPE_BERLIN, day: "numeric" }).format(d),
    );
    const month = new Intl.DateTimeFormat("en-US", {
        timeZone: EUROPE_BERLIN,
        month: "long",
    }).format(d);
    return `${ordinal(day)} ${month}`;
}

/**
 * Calendar date (yyyy-MM-dd) for an instant in Europe/Berlin (CET/CEST).
 * Used so date-range filters match timestamps shown in Berlin local time.
 */
function getCalendarYmdEuropeBerlin(input: Date | string | number): string {
    const d = input instanceof Date ? input : new Date(input);
    if (Number.isNaN(d.getTime())) return "";
    return new Intl.DateTimeFormat("sv-SE", {
        timeZone: EUROPE_BERLIN,
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
    }).format(d);
}

/** Date + time in Europe/Berlin for display (observes DST). */
export function formatDateTimeEuropeBerlin(input: Date | string | number, locale?: string): string {
    const d = input instanceof Date ? input : new Date(input);
    if (Number.isNaN(d.getTime())) return "";

    return new Intl.DateTimeFormat(locale, {
        timeZone: EUROPE_BERLIN,
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
        hour: "2-digit",
        minute: "2-digit",
    }).format(d);
}

/** Inclusive range on Berlin calendar yyyy-MM-dd strings (from date filter UI). */
export function eventAtMatchesBerlinDateFilter(
    cellIso: string | null | undefined,
    filterValue: unknown,
): boolean {
    const { from, to } = parseColumnDateRangeFilter(filterValue);
    if (!from && !to) return true;
    const ymd = getCalendarYmdEuropeBerlin(cellIso ?? "");
    if (!ymd) return false;
    if (from && ymd < from) return false;
    if (to && ymd > to) return false;
    return true;
}
